import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Assignment as ExamIcon,
  Schedule as ScheduleIcon,
  Grade as GradeIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { motion } from 'framer-motion';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

const ExamManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [stats, setStats] = useState({
    totalExams: 0,
    upcomingExams: 0,
    completedExams: 0,
    activeExams: 0
  });

  const [formData, setFormData] = useState({
    exam_name: '',
    exam_type: 'class_test',
    custom_exam_type: '',
    class_id: '',
    subject_id: '',
    exam_date: new Date(),
    start_time: '',
    end_time: '',
    total_marks: '',
    passing_marks: '',
    description: '',
    is_active: true
  });

  const examTypes = [
    { value: 'class_test', label: 'Class Test' },
    { value: 'monthly_test', label: 'Monthly Test' },
    { value: 'terminal_test', label: 'Terminal Test' },
    { value: 'half_yearly', label: 'Half Yearly Test' },
    { value: 'final_test', label: 'Final Test' },
    { value: 'practical_test', label: 'Practical Test' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchExams();
    fetchClasses();
    fetchSubjects();
    fetchStats();
  }, []);

  const fetchExams = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/exams');
      if (response.data.success) {
        setExams(response.data.data || []);
      } else {
        // Handle case when backend returns success: false
        setExams([]);
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
      // Only show error message for actual errors, not for empty data
      if (error.response?.status !== 200) {
        enqueueSnackbar('Failed to fetch exams', { variant: 'error' });
      }
      setExams([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get('/api/classes');
      setClasses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get('/api/subjects');
      if (response.data.success) {
        setSubjects(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/exams/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const examData = {
        ...formData,
        exam_type: formData.exam_type === 'other' ? formData.custom_exam_type : formData.exam_type,
        exam_date: format(formData.exam_date, 'yyyy-MM-dd')
      };
      delete examData.custom_exam_type;
      
      const url = editingExam ? `/api/exams/${editingExam.exam_id}` : '/api/exams';
      const method = editingExam ? 'put' : 'post';
      
      const response = await axios[method](url, examData);
      
      if (response.data.success) {
        enqueueSnackbar(
          editingExam ? 'Exam updated successfully' : 'Exam created successfully',
          { variant: 'success' }
        );
        setOpenDialog(false);
        resetForm();
        fetchExams();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Operation failed',
        { variant: 'error' }
      );
    }
  };

  const handleEdit = (exam) => {
    setEditingExam(exam);
    setFormData({
      exam_name: exam.exam_name || '',
      exam_type: examTypes.some(t => t.value === exam.exam_type) ? exam.exam_type : 'other',
      custom_exam_type: examTypes.some(t => t.value === exam.exam_type) ? '' : (exam.exam_type || ''),
      class_id: exam.class_id || '',
      subject_id: exam.subject_id || '',
      exam_date: new Date(exam.exam_date) || new Date(),
      start_time: exam.start_time || '',
      end_time: exam.end_time || '',
      total_marks: exam.total_marks || '',
      passing_marks: exam.passing_marks || '',
      description: exam.description || '',
      is_active: exam.is_active !== undefined ? exam.is_active : true
    });
    setOpenDialog(true);
  };

  const handleDelete = async (examId) => {
    if (window.confirm('Are you sure you want to delete this exam?')) {
      try {
        const response = await axios.delete(`/api/exams/${examId}`);
        if (response.data.success) {
          enqueueSnackbar('Exam deleted successfully', { variant: 'success' });
          fetchExams();
          fetchStats();
        }
      } catch (error) {
        enqueueSnackbar('Failed to delete exam', { variant: 'error' });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      exam_name: '',
      exam_type: 'class_test',
      custom_exam_type: '',
      class_id: '',
      subject_id: '',
      exam_date: new Date(),
      start_time: '',
      end_time: '',
      total_marks: '',
      passing_marks: '',
      description: '',
      is_active: true
    });
    setEditingExam(null);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'class_test': return 'info';
      case 'monthly_test': return 'primary';
      case 'terminal_test': return 'warning';
      case 'half_yearly': return 'secondary';
      case 'final_test': return 'error';
      case 'practical_test': return 'success';
      default: return 'default';
    }
  };

  const getTypeLabel = (type) => {
    const found = examTypes.find(t => t.value === type);
    return found ? found.label : type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'N/A';
  };

  const getStatusColor = (examDate) => {
    const today = new Date();
    const exam = new Date(examDate);
    if (exam < today) return 'success'; // Completed
    if (exam.toDateString() === today.toDateString()) return 'warning'; // Today
    return 'info'; // Upcoming
  };

  const getStatusLabel = (examDate) => {
    const today = new Date();
    const exam = new Date(examDate);
    if (exam < today) return 'Completed';
    if (exam.toDateString() === today.toDateString()) return 'Today';
    return 'Upcoming';
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
          Exam Management
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {stats.totalExams}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Total Exams
                      </Typography>
                    </Box>
                    <ExamIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {stats.upcomingExams}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Upcoming Exams
                      </Typography>
                    </Box>
                    <ScheduleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {stats.completedExams}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Completed Exams
                      </Typography>
                    </Box>
                    <GradeIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {stats.activeExams}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Active Exams
                      </Typography>
                    </Box>
                    <AssessmentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Exam Schedule" />
            <Tab label="Results" />
          </Tabs>
        </Paper>

        {/* Exam Schedule Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* Controls */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchExams}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setOpenDialog(true);
                }}
              >
                Schedule Exam
              </Button>
            </Box>
          </Paper>

          {/* Exams Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Exam Details</TableCell>
                    <TableCell>Class & Subject</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Marks</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">Loading...</TableCell>
                    </TableRow>
                  ) : (Array.isArray(exams) ? exams : []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">No exams found</TableCell>
                    </TableRow>
                  ) : (
                    (Array.isArray(exams) ? exams : []).map((exam) => (
                      <TableRow key={exam.exam_id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                              {exam.exam_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {exam.instructions ? exam.instructions.substring(0, 50) + '...' : 'No instructions'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {exam.class_name} - {exam.section_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {exam.subject_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {format(new Date(exam.exam_date), 'dd/MM/yyyy')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {exam.start_time} - {exam.end_time}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">Total: {exam.total_marks}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Pass: {exam.passing_marks}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getTypeLabel(exam.exam_type)}
                            color={getTypeColor(exam.exam_type)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(exam.exam_date)}
                            color={getStatusColor(exam.exam_date)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Edit Exam">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(exam)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Exam">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(exam.exam_id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </TabPanel>

        {/* Results Tab */}
        <TabPanel value={tabValue} index={1}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Exam Results
            </Typography>
            <Typography color="text.secondary">
              Exam results and grade management will be implemented here.
            </Typography>
          </Paper>
        </TabPanel>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingExam ? 'Edit Exam' : 'Schedule New Exam'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Exam Name"
                  value={formData.exam_name}
                  onChange={(e) => setFormData({ ...formData, exam_name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Exam Type</InputLabel>
                  <Select
                    value={formData.exam_type}
                    label="Exam Type"
                    onChange={(e) => setFormData({ ...formData, exam_type: e.target.value })}
                  >
                    {(Array.isArray(examTypes) ? examTypes : []).map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Class</InputLabel>
                  <Select
                    value={formData.class_id}
                    label="Class"
                    onChange={(e) => setFormData({ ...formData, class_id: e.target.value })}
                  >
                    {(Array.isArray(classes) ? classes : []).map((classItem) => (
                      <MenuItem key={classItem.class_id} value={classItem.class_id}>
                        {classItem.class_name} - {classItem.section_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Subject</InputLabel>
                  <Select
                    value={formData.subject_id}
                    label="Subject"
                    onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                  >
                    {(Array.isArray(subjects) ? subjects : []).map((subject) => (
                      <MenuItem key={subject.subject_id} value={subject.subject_id}>
                        {subject.subject_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="Exam Date"
                  value={formData.exam_date}
                  onChange={(newValue) => setFormData({ ...formData, exam_date: newValue })}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Total Marks"
                  type="number"
                  value={formData.total_marks}
                  onChange={(e) => setFormData({ ...formData, total_marks: e.target.value })}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Passing Marks"
                  type="number"
                  value={formData.passing_marks}
                  onChange={(e) => setFormData({ ...formData, passing_marks: e.target.value })}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>
              {formData.exam_type === 'other' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Specify Exam Type"
                    value={formData.custom_exam_type}
                    onChange={(e) => setFormData({ ...formData, custom_exam_type: e.target.value })}
                    required
                    placeholder="Enter custom exam type..."
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description / Instructions"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Exam instructions for students..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingExam ? 'Update' : 'Schedule'} Exam
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ExamManagement;
